import React, { useEffect } from "react";
import styled from "styled-components";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { device } from "../lib/breakpoints";
import { APP_NAME } from "../helpers/constants";
import SEO from "../components/SEO";
import logo from "../assets/logo.png";
import { trackActivityEvent } from "../lib/analytics/trackActivityEvent";

const NotFound = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    trackActivityEvent("error_404", {
      path: location.pathname + location.search,
    });
  }, [location.pathname, location.search]);

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <Container>
      <SEO
        title={`Page Not Found | ${APP_NAME}`}
        description={`The page you're looking for doesn't exist. Return to ${APP_NAME} to explore our AI-powered property management platform for independent landlords.`}
        keywords={`404, page not found, ${APP_NAME}`}
        imageAlt={`${APP_NAME} 404 page not found`}
        category="Error"
        tags={["404", "page not found"]}
      />

      <Content>
        <ErrorCode>404</ErrorCode>
        <Title>Page Not Found</Title>
        <Subtitle>
          Oops! The page you're looking for doesn't exist. It might have been
          moved, deleted, or it doesn't exist yet.
        </Subtitle>

        <ActionButtons>
          <PrimaryButton onClick={handleGoBack}>← Go Back</PrimaryButton>
          <SecondaryButton to="/">Return to Home</SecondaryButton>
        </ActionButtons>
      </Content>
    </Container>
  );
};

export default NotFound;

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-background);
  padding: 2rem;
`;

const Content = styled.div`
  max-width: 600px;
  text-align: center;
  background: var(--color-bg-secondary);
  padding: 4rem 3rem;
  border-radius: 24px;
  box-shadow: 0 20px 40px var(--shadow-light);
  border: 1.5px solid var(--color-border-gold);
  animation: fadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  @media ${device.mobile} {
    padding: 3rem 1.25rem;
  }
`;

const ErrorCode = styled.div`
  font-size: 7rem;
  font-weight: 900;
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-fill-color: transparent;
  line-height: 1;
  margin-bottom: 1rem;
  text-shadow: 0 4px 24px rgba(44, 95, 45, 0.3);
  font-family: var(--font-primary);
  letter-spacing: -0.03em;
  @media ${device.mobile} {
    font-size: 4.5rem;
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--color-primary);
  margin-bottom: 1rem;
  line-height: 1.2;
  font-family: var(--font-primary);
  letter-spacing: -0.02em;
  @media ${device.mobile} {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.15rem;
  color: var(--color-text-light);
  line-height: 1.6;
  margin-bottom: 2.5rem;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
  font-family:
    "Inter",
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;
  letter-spacing: -0.005em;
  @media ${device.mobile} {
    font-size: 1rem;
    margin-bottom: 2rem;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1.25rem;
  justify-content: center;
  margin-bottom: 3rem;
  flex-wrap: wrap;
  @media ${device.mobile} {
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
  }
`;

const PrimaryButton = styled.button`
  background: var(--gradient-primary);
  color: white;
  border: none;
  padding: 1rem 2.25rem;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 150px;
  font-family: var(--font-secondary);
  box-shadow: 0 4px 15px rgba(44, 95, 45, 0.3);
  letter-spacing: -0.01em;
  &:hover {
    background: var(--gradient-primary-dark);
    transform: scale(1.04);
    box-shadow: 0 8px 25px rgba(44, 95, 45, 0.4);
  }
  @media ${device.mobile} {
    width: 100%;
    max-width: 220px;
  }
`;

const SecondaryButton = styled(Link)`
  background: white;
  color: var(--color-primary);
  border: 2px solid var(--color-primary);
  padding: 1rem 2.25rem;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1.1rem;
  text-decoration: none;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 150px;
  display: inline-block;
  font-family: var(--font-secondary);
  letter-spacing: -0.01em;
  &:hover {
    background: var(--color-primary);
    color: white;
    transform: scale(1.03);
    box-shadow: 0 8px 25px rgba(44, 95, 45, 0.3);
  }
  @media ${device.mobile} {
    width: 100%;
    max-width: 220px;
  }
`;

const HelpSection = styled.div`
  border-top: 1px solid var(--color-border-light);
  padding-top: 2rem;
`;

const HelpTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--color-accent);
  margin-bottom: 1rem;
  font-family:
    "Inter",
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;
`;

const HelpLinks = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
  @media ${device.mobile} {
    gap: 0.5rem;
  }
`;

const LogoSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 2rem;
`;

const Logo = styled.img`
  width: 80px;
  height: 80px;
  margin-bottom: 1rem;

  @media ${device.mobile} {
    width: 60px;
    height: 60px;
  }
`;

const LogoText = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--color-primary);
  margin: 0;
  font-family: var(--font-primary);

  @media ${device.mobile} {
    font-size: 1.25rem;
  }
`;

const HelpLink = styled(Link)`
  color: var(--color-primary);
  text-decoration: none;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 0.98rem;
  font-family: var(--font-secondary);
  &:hover {
    color: white;
    background: var(--color-primary);
    transform: translateY(-2px) scale(1.04);
    box-shadow: 0 4px 12px rgba(44, 95, 45, 0.3);
  }
`;
